import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { recentEntries, RECENT_LIMIT } from '@/lib/utils';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator } from '@/components/ui/command';
import { ArrowUp, ArrowDown, Plus, Check, CaretUpDown, ArrowLeft, PencilSimple, Trash, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useCan } from '@/lib/permissions';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

const INCOME_CATEGORIES = ['Service Income', 'Consultation Fee', 'Commission', 'Interest', 'Other Income'];
const EXPENSE_CATEGORIES = ['Office Rent', 'Electricity', 'Internet / Phone', 'Stationery', 'Staff Salary', 'Travel', 'Equipment', 'Maintenance', 'Printing', 'Commission Paid', 'Bank Charges', 'Miscellaneous', 'Other Expense'];

// Categories the system creates automatically (invoice payments, transfers, advances, etc.)
// — they are never offered as picks for a manual income/expense entry.
const RESERVED_SYSTEM_CATEGORIES = new Set([
  'invoice payment', 'govt fee deduction', 'govt fee collected',
  'customer advance', 'customer advance used',
  'transfer in', 'transfer out',
  'opening balance', 'balance adjustment',
  'reimbursement', 'refund received',
]);

export function CategoryCombo({ categories, value, onChange, type, testId, headGroups = [], onSelectHead }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const presets = type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
  const userCats = (categories || []).filter(c => c && !RESERVED_SYSTEM_CATEGORIES.has(c.toLowerCase()));
  const allCats = [...new Set([...presets, ...userCats])].filter(c => c);
  const match = (c) => !search || c.toLowerCase().includes(search.toLowerCase());
  const filtered = allCats.filter(match);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between rounded-lg h-9 text-sm font-normal border-[#E4E4E7] hover:border-[#A1A1AA] hover:bg-white mt-1" data-testid={testId}>
          <span className={value ? 'text-[#0A0A0A]' : 'text-[#A1A1AA]'}>{value || (type === 'income' ? 'Select category...' : 'Select head...')}</span>
          <CaretUpDown size={12} className="ml-1 shrink-0 text-[#A1A1AA]" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 rounded-lg" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search or type new..." value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty className="py-2 text-center text-xs text-[#A1A1AA]">No match</CommandEmpty>
            <CommandGroup heading={headGroups.length ? 'Expense Heads' : undefined}>
              {filtered.map(c => (
                <CommandItem key={c} value={c} onSelect={() => { onChange(c); setOpen(false); setSearch(''); }} className="cursor-pointer text-sm">
                  <span className="flex-1">{c}</span>
                  {c === value && <Check size={14} className="text-[#3B82F6]" />}
                </CommandItem>
              ))}
            </CommandGroup>
            {headGroups.map(g => {
              const items = g.items.filter(a => match(a.name));
              if (!items.length) return null;
              return (
                <CommandGroup key={g.label} heading={g.label}>
                  {items.map(a => (
                    <CommandItem key={a.id} value={a.id} onSelect={() => { onSelectHead(a); setOpen(false); setSearch(''); }} className="cursor-pointer text-sm" data-testid={`head-option-${a.id}`}>
                      <span className="flex-1">{a.name}</span>
                      <span className="text-[10px] font-mono text-[#A1A1AA]">{fmt(a.balance || 0)}</span>
                      {a.name === value && <Check size={14} className="ml-1 text-[#3B82F6]" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              );
            })}
            {search && !filtered.includes(search) && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem onSelect={() => { onChange(search); setOpen(false); setSearch(''); }} className="cursor-pointer text-[#3B82F6] font-medium text-sm">
                    <Plus size={14} className="mr-1.5" /> Add &quot;{search}&quot;
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Shared page for recording either an Income or an Expense.
 * Used by routes /income and /expense via the `type` prop.
 */
export default function TransactionPage({ type }) {
  const navigate = useNavigate();
  const isIncome = type === 'income';
  const canEdit = useCan(type, 'edit');
  const canDelete = useCan(type, 'delete');
  const accent = isIncome
    ? { color: 'text-[#10B981]', bg: 'bg-[#10B981]', hover: 'hover:bg-[#059669]', icon: ArrowUp, soft: 'bg-[#ECFDF5]', border: 'border-[#A7F3D0]' }
    : { color: 'text-[#EF4444]', bg: 'bg-[#EF4444]', hover: 'hover:bg-[#DC2626]', icon: ArrowDown, soft: 'bg-[#FEF2F2]', border: 'border-[#FECACA]' };
  const Icon = accent.icon;

  const [accounts, setAccounts] = useState([]);
  const [recent, setRecent] = useState([]);
  // Default view = today's entries only. The date picker in the Recent
  // section header lets the user jump to any historical day.
  const [filterDate, setFilterDate] = useState('');
  const [customCategories, setCustomCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const initialForm = () => ({
    category: '', amount: '', account: '', description: '',
    date: new Date().toISOString().split('T')[0],
    invoice_id: '', to_account: '',
  });
  const [form, setForm] = useState(initialForm);
  // Phase B — optional list of recent invoices for linking an expense to a
  // specific bill (true per-bill profit attribution). Loaded lazily for
  // expense pages only.
  const [linkableInvoices, setLinkableInvoices] = useState([]);

  // Inline edit & delete state for the Recent list. Editing reuses the same
  // `form` state above; `editing` holds the txn being edited so handleSubmit
  // can dispatch to PUT instead of POST.
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Bulk-delete state for the Recent Income/Expense register.
  const [bulkSelected, setBulkSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(false);

  const fetchData = useCallback(() => {
    Promise.all([
      api.get('/finance/accounts'),
      api.get(`/finance?type=${type}`),
      type === 'expense' ? api.get('/finance?type=transfer_out') : Promise.resolve({ data: [] }),
    ]).then(([accRes, txnRes, trfRes]) => {
      setAccounts(accRes.data);
      // Hide auto-posted receipt-voucher rows on the Record Income page —
      // those are invoice payments (RCP-*) which already have their own
      // Receipts list. Keep manual Income entries only. Expense page is
      // unaffected (no synthetic system rows there).
      const data = (txnRes.data || []).filter(t => t.category !== 'Invoice Payment');
      // Keep the full set in state; the render filters down to the
      // currently picked day. This way the date picker can roam over
      // historical entries without re-hitting the API.
      const names = Object.fromEntries((accRes.data || []).map(a => [a.id, a.name]));
      const headRows = (trfRes.data || []).filter(t => t.payment_head)
        .map(t => ({ ...t, isHead: true, category: names[t.to_account] || 'Payment' }));
      setRecent([...data, ...headRows]);
      const cats = [...new Set(data.map(t => t.category).filter(Boolean))];
      setCustomCategories(cats);
    }).catch(console.error).finally(() => setLoading(false));
  }, [type]);

  useEffect(() => { setLoading(true); fetchData(); }, [fetchData]);

  // Phase B — load last 30-day invoices once for the "Link to invoice" dropdown
  // on the Expense form. Skipped entirely for the Income page.
  useEffect(() => {
    if (isIncome) return;
    const from = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
    api.get(`/invoices?date_from=${from}`)
      .then(r => setLinkableInvoices((r.data || []).slice(0, 200)))
      .catch(() => {});
  }, [isIncome]);

  const handleSubmit = async () => {
    if (!form.category || !form.amount || !form.account) {
      toast.error('Fill all required fields');
      return;
    }
    const amt = parseFloat(form.amount);
    if (isNaN(amt) || amt <= 0) { toast.error('Amount must be greater than 0'); return; }
    if (!isIncome && !editing) {
      const accObj = accounts.find(a => a.id === form.account);
      // Credit cards & creditor accounts may freely swing further negative
      // — they're liability-side, so accruing more debt is normal accounting.
      // Skip the cash-on-hand guard for those types.
      const allowNegative = accObj && (accObj.type === 'credit_card' || accObj.type === 'creditor');
      if (accObj && !allowNegative && (accObj.balance || 0) < amt) {
        toast.error(`Insufficient balance in ${accObj.name}: available ${fmt(accObj.balance)}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const ts = form.date + 'T' + new Date().toISOString().split('T')[1];
      if (form.to_account && !isIncome) {
        const body = { from_account: form.account, to_account: form.to_account, amount: amt, description: form.description || `Payment to ${form.category}` };
        if (editing?.isHead) await api.put(`/finance/transfers/${editing.transfer_id}`, { ...body, date: form.date });
        else {
          if (editing) await api.delete(`/finance/${editing.id}`);
          await api.post('/finance/transfer', { ...body, created_at: ts, payment_head: true });
        }
        toast.success(`Payment to ${form.category} ${editing ? 'updated' : 'recorded'}`);
        setEditing(null);
      } else if (editing?.isHead) {
        await api.delete(`/finance/${editing.id}`);
        await api.post('/finance', { type, category: form.category, amount: amt, account: form.account, description: form.description, created_at: ts, invoice_id: form.invoice_id || null });
        toast.success('Payment updated');
        setEditing(null);
      } else if (editing) {
        await api.put(`/finance/${editing.id}`, {
          type, category: form.category, amount: amt,
          account: form.account, description: form.description, created_at: ts,
          ...(isIncome ? {} : { invoice_id: form.invoice_id || null }),
        });
        toast.success(`${editing.voucher_number || 'Transaction'} updated`);
        setEditing(null);
      } else {
        const { data: created } = await api.post('/finance', {
          type, category: form.category, amount: amt,
          account: form.account, description: form.description, created_at: ts,
          ...(isIncome ? {} : { invoice_id: form.invoice_id || null }),
        });
        if (created?.approval_status === 'pending') {
          toast.success('Expense submitted — pending admin approval (above the configured threshold).', { duration: 5000 });
        } else {
          toast.success(`${isIncome ? 'Income' : 'Payment'} recorded`);
        }
      }
      setForm(initialForm());
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const accountName = (id) => accounts.find(a => a.id === id)?.name || '-';

  // Income / Expense entries can only touch real money accounts.
  // Hide creditor / debtor / capital / asset accounts from selection.
  // Credit cards are only usable when balance ≤ 0. Cash / bank / wallet /
  // creditor accounts are always available regardless of balance.
  const moneyAccounts = accounts.filter(a => {
    if (!isIncome) return ['cash', 'bank', 'wallet'].includes(a.type);
    if (['cash', 'bank', 'wallet', 'creditor'].includes(a.type)) return true;
    if (a.type === 'credit_card') return (a.balance || 0) <= 0;
    return false;
  });

  const headGroups = isIncome ? [] : [
    { label: 'Creditors', items: accounts.filter(a => a.type === 'creditor' && !a.system) },
    { label: 'Capital', items: accounts.filter(a => a.type === 'capital') },
  ];

  const startEdit = (txn) => {
    setEditing(txn);
    setForm({
      category: txn.category || '',
      amount: String(txn.amount || ''),
      account: txn.account || '',
      description: (txn.isHead ? (txn.description || '').replace(/\s*\(to .+?\)$/, '') : txn.description) || '',
      date: (txn.created_at || '').split('T')[0] || new Date().toISOString().split('T')[0],
      invoice_id: txn.invoice_id || '',
      to_account: txn.isHead ? txn.to_account : '',
    });
    document.querySelector(`[data-testid="${type}-form-card"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(initialForm());
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/finance/${confirmDelete.id}`);
      toast.success('Transaction deleted');
      if (editing && editing.id === confirmDelete.id) cancelEdit();
      setConfirmDelete(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const performBulkDelete = async () => {
    if (bulkSelected.size === 0) return;
    setBulkDeleting(true);
    let ok = 0, failed = 0;
    for (const id of bulkSelected) {
      try {
        await api.delete(`/finance/${id}`);
        ok++;
      } catch {
        failed++;
      }
    }
    setBulkDeleting(false);
    setConfirmBulk(false);
    if (editing && bulkSelected.has(editing.id)) cancelEdit();
    setBulkSelected(new Set());
    if (ok > 0) toast.success(`Deleted ${ok} ${isIncome ? 'income' : 'payment'} entr${ok === 1 ? 'y' : 'ies'}${failed ? ` (${failed} failed)` : ''}`);
    if (ok === 0 && failed > 0) toast.error(`Failed to delete ${failed} entr${failed === 1 ? 'y' : 'ies'}`);
    fetchData();
  };

  if (loading) return <div className="p-8 text-sm text-[#52525B]">Loading...</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid={`${type}-page`}>
      {/* Header — back link only; title/tagline are now rendered in the sticky topbar */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <button
          onClick={() => navigate('/reports')}
          className="inline-flex items-center gap-1 text-xs text-[#52525B] hover:text-[#0A0A0A]"
          data-testid="back-to-balance-sheet"
        >
          <ArrowLeft size={12} /> Back to Reports
        </button>
      </div>

      {/* Form Card — only when staff has edit rights for this page */}
      {canEdit && (
      <Card className="rounded-xl border-[#E4E4E7] shadow-sm max-w-3xl" data-testid={`${type}-form-card`}>
        <CardContent className="p-5 sm:p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }} data-testid={`${type}-form-heading`}>
                {editing ? <>Edit <span className="text-[#3B82F6]">{editing.voucher_number || ''}</span></> : (isIncome ? 'New Income' : 'New Payment')}
              </h2>
              <p className="text-xs text-[#52525B] mt-0.5">
                {editing ? 'Account balance will adjust to reflect the new values.' : (isIncome ? 'Record money received outside an invoice.' : 'Pay an expense, a creditor (loan / card) or capital drawings from Cash or Bank.')}
              </p>
            </div>
            {editing && (
              <Button variant="ghost" size="sm" onClick={cancelEdit} className="rounded-lg text-[#52525B]" data-testid={`cancel-edit-${type}`}>
                <X size={14} className="mr-1" /> Cancel
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Date *</Label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-lg mt-1" data-testid="txn-date-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Account *</Label>
              <Select value={form.account} onValueChange={v => setForm({ ...form, account: v })}>
                <SelectTrigger className="rounded-lg mt-1" data-testid="txn-account-select"><SelectValue placeholder="Select account..." /></SelectTrigger>
                <SelectContent>
                  {moneyAccounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      <span className="flex items-center justify-between gap-3 w-full">
                        <span>{a.name} <span className="text-[10px] text-[#A1A1AA] uppercase tracking-wide">({(a.type || '').replace('_', ' ')})</span></span>
                        <span className={`text-xs font-mono ${(a.balance || 0) > 0 ? 'text-[#10B981]' : (a.balance || 0) < 0 ? 'text-[#EF4444]' : 'text-[#A1A1AA]'}`}>{fmt(a.balance || 0)}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">{isIncome ? 'Category *' : 'Head *'}</Label>
            <CategoryCombo
              categories={customCategories}
              value={form.category}
              onChange={v => setForm({ ...form, category: v, to_account: '' })}
              type={type}
              testId="txn-category-select"
              headGroups={headGroups}
              onSelectHead={a => setForm({ ...form, category: a.name, to_account: a.id, invoice_id: '' })}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Amount *</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#A1A1AA]">&#8377;</span>
                <Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="0" className="rounded-lg pl-7 font-mono" data-testid="txn-amount-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Description</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional note" className="rounded-lg mt-1" data-testid="txn-description-input" />
            </div>
            {!isIncome && !form.to_account && (
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Link to Invoice <span className="text-[#A1A1AA] normal-case">(optional — for per-bill profit)</span></Label>
                <select
                  value={form.invoice_id || ''}
                  onChange={e => setForm({ ...form, invoice_id: e.target.value })}
                  className="rounded-lg mt-1 h-9 w-full border border-[#E4E4E7] bg-white px-2 text-sm"
                  data-testid="txn-invoice-link"
                >
                  <option value="">— Not linked to any invoice —</option>
                  {linkableInvoices.map(inv => (
                    <option key={inv.id} value={inv.id}>
                      {`${inv.invoice_number} · ${inv.customer_name} · ${fmt(inv.total)}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            {editing && (
              <Button variant="outline" onClick={cancelEdit} className="rounded-xl border-[#E4E4E7]" data-testid="txn-cancel-btn">Cancel</Button>
            )}
            {!editing && (
              <Button variant="outline" onClick={() => setForm(initialForm())} className="rounded-xl border-[#E4E4E7]" data-testid="txn-reset-btn">Reset</Button>
            )}
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className={`text-white rounded-xl ${accent.bg} ${accent.hover} hover:text-white`}
              data-testid="save-transaction-btn"
            >
              <Icon size={14} className="mr-1" weight="bold" />
              {submitting ? 'Saving...' : (editing ? 'Save Changes' : `Save ${isIncome ? 'Income' : 'Payment'}`)}
            </Button>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Recent List */}
      <Card className="rounded-xl border-[#E4E4E7] shadow-sm" data-testid="recent-transactions-card">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                Recent {isIncome ? 'Income' : 'Payments'}
              </h2>
              <p className="text-xs text-[#52525B]" data-testid={`${type}-recent-caption`}>{filterDate ? `Showing entries dated ${new Date(filterDate).toLocaleDateString('en-IN')}` : `Showing last ${RECENT_LIMIT} entries`}</p>
            </div>
            <div className="flex items-center gap-2">
              {/* Date picker — default is today. */}
              <Input
                type="date"
                value={filterDate}
                onChange={(e) => { setFilterDate(e.target.value); setBulkSelected(new Set()); }}
                className="h-8 rounded-lg text-xs w-36"
                data-testid={`${type}-recent-date-filter`}
              />
<Button variant="ghost" size="sm" className={`h-8 px-2 text-xs ${!filterDate ? 'bg-[#EFF6FF] text-[#1D4ED8] font-semibold' : 'text-[#52525B]'}`}
                onClick={() => { setFilterDate(''); setBulkSelected(new Set()); }}
                data-testid={`${type}-recent-last-btn`}>
                Last 25
              </Button>
              <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-[#52525B]"
                onClick={() => { setFilterDate(new Date().toISOString().split('T')[0]); setBulkSelected(new Set()); }}
                data-testid={`${type}-recent-today-btn`}>
                Today
              </Button>
              {bulkSelected.size > 0 && canDelete && (
                <div className="flex items-center gap-2" data-testid={`${type}-bulk-bar`}>
                  <span className="text-xs text-[#991B1B] font-semibold">{bulkSelected.size} selected</span>
                  <Button variant="ghost" size="sm" onClick={() => setBulkSelected(new Set())} className="text-xs h-7" data-testid={`bulk-clear-${type}`}>
                    Clear
                  </Button>
                  <Button size="sm" onClick={() => setConfirmBulk(true)} className="bg-[#EF4444] text-white hover:bg-[#DC2626] hover:text-white rounded-lg text-xs h-7" data-testid={`bulk-delete-${type}-btn`}>
                    <Trash size={12} className="mr-1" /> Delete Selected
                  </Button>
                </div>
              )}
            </div>
          </div>
          {(() => {
            // Filter by picked day. Falls back to created_at when no
            // explicit `date` was stored.
            const displayed = recentEntries(recent, filterDate);
            if (displayed.length === 0) {
              return <p className="text-sm text-[#A1A1AA] italic text-center py-6" data-testid={`${type}-recent-empty`}>No {isIncome ? 'income' : 'payment'} entries {filterDate ? `on ${new Date(filterDate).toLocaleDateString('en-IN')}` : 'yet'}</p>;
            }
            return (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E4E4E7]">
                  {canDelete && (
                    <TableHead className="w-8 px-2">
                      {(() => {
                        const allTicked = displayed.length > 0 && displayed.every(t => bulkSelected.has(t.id));
                        return (
                          <input
                            type="checkbox"
                            checked={allTicked}
                            onChange={e => {
                              if (e.target.checked) setBulkSelected(new Set(displayed.map(t => t.id)));
                              else setBulkSelected(new Set());
                            }}
                            className="h-3.5 w-3.5 rounded border-[#3B82F6] accent-[#3B82F6] cursor-pointer"
                            title={allTicked ? 'Clear selection' : 'Select all'}
                            data-testid={`bulk-select-all-${type}`}
                          />
                        );
                      })()}
                    </TableHead>
                  )}
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Date</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Voucher</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">{isIncome ? 'Category' : 'Head'}</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Account</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Description</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Amount</TableHead>
                  {(canEdit || canDelete) && <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right w-[80px]">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayed.map(t => (
                  <TableRow key={t.id} className={`border-[#E4E4E7] group ${editing?.id === t.id ? 'bg-[#EFF6FF]' : (bulkSelected.has(t.id) ? 'bg-[#FEF2F2]' : '')}`}>
                    {canDelete && (
                      <TableCell className="w-8 px-2">
                        <input
                          type="checkbox"
                          checked={bulkSelected.has(t.id)}
                          onChange={e => {
                            setBulkSelected(prev => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(t.id); else next.delete(t.id);
                              return next;
                            });
                          }}
                          className="h-3.5 w-3.5 rounded border-[#3B82F6] accent-[#3B82F6] cursor-pointer"
                          title="Select for bulk delete"
                          data-testid={`bulk-select-${type}-${t.id}`}
                        />
                      </TableCell>
                    )}
                    <TableCell className="text-xs text-[#52525B]">{new Date(t.created_at).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell>
                      {t.voucher_number
                        ? <span className={`rounded-md text-[10px] px-2 py-0.5 ${isIncome ? 'bg-[#DCFCE7] text-[#15803D] border border-[#BBF7D0]' : 'bg-[#FEE2E2] text-[#B91C1C] border border-[#FECACA]'}`}>{t.voucher_number}</span>
                        : <span className="text-[10px] text-[#A1A1AA]">—</span>}
                    </TableCell>
                    <TableCell className="text-xs font-medium">
                      {t.category}
                      {t.isHead && <span className="ml-1.5 rounded bg-[#EEF2FF] px-1.5 py-0.5 text-[9px] font-semibold uppercase text-[#4338CA]">{accounts.find(a => a.id === t.to_account)?.type || 'head'}</span>}
                    </TableCell>
                    <TableCell className="text-xs text-[#52525B]">{accountName(t.account)}</TableCell>
                    <TableCell className="text-xs text-[#52525B] max-w-[260px] truncate" title={t.description}>{t.description || '-'}</TableCell>
                    <TableCell className={`text-xs text-right font-mono font-semibold ${accent.color}`}>{fmt(t.amount)}</TableCell>
                    {(canEdit || canDelete) && (
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                          {canEdit && (
                            <button
                              onClick={() => startEdit(t)}
                              className="p-1.5 rounded-md hover:bg-[#F4F4F5] text-[#52525B] hover:text-[#0A0A0A]"
                              title="Edit"
                              data-testid={`edit-txn-${t.id}`}
                            >
                              <PencilSimple size={13} />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => setConfirmDelete(t)}
                              className="p-1.5 rounded-md hover:bg-[#FEE2E2] text-[#52525B] hover:text-[#DC2626]"
                              title="Delete"
                              data-testid={`delete-txn-${t.id}`}
                            >
                              <Trash size={13} />
                            </button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            );
          })()}
        </CardContent>
      </Card>

      {/* Delete Confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {isIncome ? 'income' : 'payment'}?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete && (
                <>
                  <span className="font-medium text-[#0A0A0A]">{confirmDelete.category}</span> &middot; {fmt(confirmDelete.amount)} &middot; {accountName(confirmDelete.account)}
                  <br />The account balance will be reverted automatically. This cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg" data-testid="cancel-delete-txn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-[#EF4444] text-white hover:bg-[#DC2626] rounded-lg" data-testid="confirm-delete-txn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirmation. */}
      <AlertDialog open={confirmBulk} onOpenChange={(v) => { if (!v && !bulkDeleting) setConfirmBulk(false); }}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {bulkSelected.size} {isIncome ? 'income' : 'payment'} entr{bulkSelected.size === 1 ? 'y' : 'ies'}?</AlertDialogTitle>
            <AlertDialogDescription>
              Each entry's account balance will be reverted automatically. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting} className="rounded-lg" data-testid={`bulk-delete-${type}-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={bulkDeleting}
              onClick={performBulkDelete}
              className="bg-[#EF4444] text-white hover:bg-[#DC2626] rounded-lg disabled:opacity-50"
              data-testid={`bulk-delete-${type}-confirm`}
            >
              {bulkDeleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
